import { SmsConversationEventRow } from "@/src/lib/types/tables.types";
import { ClosedReason } from "@/src/models/comms/enums";
import { SmsConversationEvent, smsConversationEventShape } from "@/src/models/comms/sms-conversation-event";
import { Group } from "@/src/models/group";
import { BaseRepository } from "@/src/repo/base-repository";

const APPOINTMENT_BOOKED_REASONS: string[] = [
  ClosedReason.APPOINTMENT_BOOKED,
  ClosedReason.APPOINTMENT_BOOKED_BY_VALERIE,
];

export const SMS_CONVERSATION_EVENT_TABLE_NAME = "sms_conversation_event";
export const fullSmsConversationEventSelect = `*`;

/**
 * Repository for the append-only SMS conversation status-transition log. Rows are
 * never mutated; new state is written via record_sms_conversation_transition()
 * (the atomic writer). This repo is for reads (metrics, history) plus the raw
 * insert path used by that writer / backfills.
 */
export class SmsConversationEventRepository extends BaseRepository<SmsConversationEvent, SmsConversationEventRow> {
  constructor() {
    super({
      tableName: SMS_CONVERSATION_EVENT_TABLE_NAME,
      selectClause: fullSmsConversationEventSelect,
      modelShape: smsConversationEventShape,
    });
  }

  /** Full transition history for a conversation, oldest first. */
  public async getByConversationId(conversationId: string, group: Group): Promise<SmsConversationEvent[]> {
    return this.getAll(
      group,
      { conditions: [{ field: "conversation_id", operator: "eq", value: conversationId }] },
      { field: "created_at", ascending: true }
    );
  }

  /** The earliest event-linked message for one workflow, used as its conversation-window boundary. */
  public async getFirstMessageEventByWorkflowId(
    workflowId: string,
    group: Group
  ): Promise<SmsConversationEvent | null> {
    const events = await this.getAll(
      group,
      {
        conditions: [
          { field: "workflow_id", operator: "eq", value: workflowId },
          { field: "message_id", operator: "isNot", value: null },
        ],
      },
      { field: "created_at", ascending: true }
    );

    return events[0] ?? null;
  }

  /**
   * Transitions that closed a scheduling conversation as "appointment booked", oldest first. Used
   * for appointment attribution (replaces the old closed-thread lookup). `created_at` is the close
   * time. Per-patient volume is tiny, so we fetch the patient's events and filter by reason.
   */
  public async getAppointmentBookedClosuresByPatientId(
    patientId: string,
    group: Group
  ): Promise<SmsConversationEvent[]> {
    const events = await this.getAll(
      group,
      { conditions: [{ field: "patient_id", operator: "eq", value: patientId }] },
      { field: "created_at", ascending: true }
    );

    return events.filter((event) => event.reason != null && APPOINTMENT_BOOKED_REASONS.includes(event.reason));
  }
}

export const smsConversationEventRepository = new SmsConversationEventRepository();
export const getSmsConversationEvent = smsConversationEventRepository.get.bind(smsConversationEventRepository);
export const getAllSmsConversationEvents = smsConversationEventRepository.getAll.bind(smsConversationEventRepository);
export const getSmsConversationEventsByConversationId =
  smsConversationEventRepository.getByConversationId.bind(smsConversationEventRepository);
export const getFirstSmsMessageEventByWorkflowId =
  smsConversationEventRepository.getFirstMessageEventByWorkflowId.bind(smsConversationEventRepository);
export const getAppointmentBookedClosuresByPatientId =
  smsConversationEventRepository.getAppointmentBookedClosuresByPatientId.bind(smsConversationEventRepository);
export const insertSmsConversationEvent = smsConversationEventRepository.insert.bind(smsConversationEventRepository);
